import React, { useMemo, useState } from 'react';
import { Badge, Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import ScheduleWeeksView from './ScheduleWeeksView';
import ScheduleTeamStats from './ScheduleTeamStats';
import OpponentMatrix, { describeOpponentRange } from './OpponentMatrix';
import ScheduleVenuesAndDates from './ScheduleVenuesAndDates';
import ScheduleWarnings, { countAttentionWarnings } from './ScheduleWarnings';
import ScheduleExtraGamesView from './ScheduleExtraGamesView';
import { isRegularSeasonIncluded } from './scheduleFormModel';
import { formatLocalDateRange } from '../../utils/scheduleFormat';

const plural = (count, singular) => `${count} ${count === 1 ? singular : `${singular}s`}`;

const SchedulePreview = ({ draft, teams = [] }) => {
  const [tab, setTab] = useState('schedule');
  const result = draft?.result;
  const warnings = useMemo(() => result?.warnings || [], [result]);
  const attentionCount = countAttentionWarnings(warnings);
  const teamNames = useMemo(
    () => new Map((result?.teamStats || []).map((t) => [t.teamId, t.teamName])),
    [result],
  );

  if (!result) return null;

  const includeRegularSeason = isRegularSeasonIncluded(draft.config);
  const extraGames = result.extraGames || [];
  const extraGameCount = result.extraGameCount ?? extraGames.length;
  const tbdGameCount = result.tbdGameCount ?? 0;
  const hasWeeks = (result.weeks || []).length > 0;
  const activeTab = !includeRegularSeason && (tab === 'teams' || tab === 'opponents') ? 'schedule' : tab;

  const summary = [
    includeRegularSeason && plural(result.totalGames ?? 0, 'game'),
    includeRegularSeason && plural(result.playableWeeks ?? 0, 'week'),
    extraGameCount > 0 && `${plural(extraGameCount, 'other game')}${tbdGameCount > 0 ? ` (${tbdGameCount} TBD)` : ''}`,
    formatLocalDateRange(result.firstGameDate, result.lastGameDate),
    includeRegularSeason && describeOpponentRange(result.minOpponentMeetings, result.maxOpponentMeetings),
  ].filter(Boolean).join(' · ');

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Stack spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Preview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{summary}</Typography>
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Schedule preview"
        >
          <Tab value="schedule" label="Schedule" />
          {includeRegularSeason && <Tab value="teams" label="Team stats" />}
          {includeRegularSeason && <Tab value="opponents" label="Opponents" />}
          <Tab value="venues" label="Venues & dates" />
          <Tab
            value="warnings"
            label={(
              <Badge
                badgeContent={attentionCount}
                color="warning"
                invisible={attentionCount === 0}
                sx={{ '& .MuiBadge-badge': { right: -12 } }}
              >
                Warnings
              </Badge>
            )}
          />
        </Tabs>
      </Box>
      {activeTab === 'schedule' && (
        <Stack spacing={3}>
          {(hasWeeks || extraGames.length === 0) && <ScheduleWeeksView result={result} teams={teams} />}
          {extraGames.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="h6">Playoffs &amp; other games</Typography>
              <ScheduleExtraGamesView games={extraGames} teams={teams} />
            </Stack>
          )}
        </Stack>
      )}
      {activeTab === 'teams' && <ScheduleTeamStats result={result} teams={teams} />}
      {activeTab === 'opponents' && <OpponentMatrix result={result} />}
      {activeTab === 'venues' && (
        <ScheduleVenuesAndDates result={result} approval={draft.approval} isApproved={draft.status === 'Approved'} />
      )}
      {activeTab === 'warnings' && <ScheduleWarnings warnings={warnings} teamNames={teamNames} />}
    </Paper>
  );
};

export default SchedulePreview;
